import React, {useEffect, useState} from 'react';
import {
    Autocomplete,
    Chip,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Box,
    Stack,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Divider,
    Avatar
} from '@mui/material';
import Api from 'src/api';
import {showNotification} from 'src/redux/reducers/notificationReducer';
import {useDispatch} from 'react-redux';
import {Group, Person, Security, CheckCircle, Cancel} from '@mui/icons-material';

interface IDjangoUser {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    is_active: boolean;
    groups: number[];
    group_names: string[];
}

interface IDjangoGroup {
    id: number;
    name: string;
}

const KullaniciRolleriTab = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const [users, setUsers] = useState<IDjangoUser[]>([]);
    const [availableGroups, setAvailableGroups] = useState<IDjangoGroup[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        try {
            const [usersData, groupsData] = await Promise.all([
                Api.getPromise('/logbook/auth/user/list/'),
                Api.getPromise('/logbook/auth/user/groups/')
            ]);
            setUsers(usersData);
            setAvailableGroups(groupsData);
        } catch (error) {
            dispatch(showNotification({message: "Veriler yüklenirken hata oluştu.", severity: "error"}));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleUpdateUser = async (user: IDjangoUser, updates: Partial<IDjangoUser>) => {
        try {
            const payload = { ...user, ...updates };
            await Api.putPromise('/logbook/auth/user/list/', payload);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, ...updates } : u));
            dispatch(showNotification({message: "Kullanıcı güncellendi.", severity: "success"}));
        } catch (error) {
            dispatch(showNotification({message: "Güncelleme başarısız.", severity: "error"}));
        }
    };

    if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography fontWeight={700}>Yükleniyor...</Typography></Box>;

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Security color="primary" />
                <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Kullanıcı Yetki Yönetimi</Typography>
            </Stack>

            {isMobile ? (
                <Stack spacing={2}>
                    {users.map((user) => (
                        <Card key={user.id} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={800}>
                                            {user.first_name} {user.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ bgcolor: 'grey.100', px: 1, borderRadius: '4px' }}>
                                            @{user.username}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="caption" fontWeight={700}>{user.is_active ? 'AKTİF' : 'PASİF'}</Typography>
                                        <Switch
                                            size="small"
                                            checked={user.is_active}
                                            onChange={(e) => handleUpdateUser(user, { is_active: e.target.checked })}
                                            color="primary"
                                        />
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 1.5, opacity: 0.5 }} />

                                <Typography variant="caption" color="textSecondary" fontWeight={800} sx={{ mb: 1, display: 'block' }}>DJANGO GRUPLARI</Typography>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={availableGroups}
                                    getOptionLabel={(option) => option.name}
                                    value={availableGroups.filter(g => user.groups.includes(g.id))}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(_, newValue) => {
                                        handleUpdateUser(user, { groups: newValue.map(v => v.id) });
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option.name}
                                                size="small"
                                                {...getTagProps({ index })}
                                                sx={{ fontWeight: 700, borderRadius: '6px' }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" placeholder="Grup Seçiniz" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                    )}
                                />
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 800 }}>Kullanıcı</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Oturum Açabilir</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Django Grupları (Yetkiler)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } }}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.main', fontSize: '0.8rem', fontWeight: 800 }}>
                                                {user.first_name[0]}{user.last_name[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={800}>
                                                    {user.first_name} {user.last_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    @{user.username}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {user.is_active ? <CheckCircle color="success" sx={{ fontSize: 16 }} /> : <Cancel color="error" sx={{ fontSize: 16 }} />}
                                            <Switch
                                                size="small"
                                                checked={user.is_active}
                                                onChange={(e) => handleUpdateUser(user, { is_active: e.target.checked })}
                                                color="primary"
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 350 }}>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={availableGroups}
                                            getOptionLabel={(option) => option.name}
                                            value={availableGroups.filter(g => user.groups.includes(g.id))}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                handleUpdateUser(user, { groups: newValue.map(v => v.id) });
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        variant="outlined"
                                                        label={option.name}
                                                        size="small"
                                                        {...getTagProps({ index })}
                                                        sx={{ fontWeight: 700, borderRadius: '6px' }}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" placeholder="Grup Seçiniz" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default KullaniciRolleriTab;
